import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TabNavigation from '../../components/ui/TabNavigation';
import MemberNotificationCard from './components/MemberNotificationCard';
import GlobalNotificationPanel from './components/GlobalNotificationPanel';
import NotificationPreview from './components/NotificationPreview';

const NotificationSettingsManagement = () => {
  const [familyMembers, setFamilyMembers] = useState([
    {
      id: 1,
      name: 'Sarah',
      role: 'Mom',
      color: '#EF4444',
      initials: 'SM'
    },
    {
      id: 2,
      name: 'Michael',
      role: 'Dad',
      color: '#3B82F6',
      initials: 'MD'
    },
    {
      id: 3,
      name: 'Emma',
      role: 'Daughter',
      color: '#EC4899',
      initials: 'ED'
    },
    {
      id: 4,
      name: 'Lucas',
      role: 'Son',
      color: '#10B981',
      initials: 'LS'
    },
    {
      id: 5,
      name: 'Olivia',
      role: 'Daughter',
      color: '#F59E0B',
      initials: 'OD'
    },
    {
      id: 6,
      name: 'Noah',
      role: 'Son',
      color: '#8B5CF6',
      initials: 'NS'
    }
  ]);

  const [notificationSettings, setNotificationSettings] = useState([]);
  const [globalSettings, setGlobalSettings] = useState({
    quietHoursStart: '22:00',
    quietHoursEnd: '07:00',
    emergencyOverride: true,
    crossPlatformSync: true,
    emergencyContacts: []
  });
  const [showPreview, setShowPreview] = useState(false);
  const [previewMember, setPreviewMember] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Load settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage?.getItem('notificationSettings');
    const savedGlobalSettings = localStorage?.getItem('globalNotificationSettings');
    
    if (savedSettings) {
      try {
        setNotificationSettings(JSON.parse(savedSettings));
      } catch (e) {
        console.error('Failed to parse notification settings', e);
      }
    } else {
      // Initialize default settings for all members
      const defaultSettings = familyMembers?.map(member => ({
        memberId: member?.id,
        enabled: true,
        messaging: {
          enabled: true,
          sound: true,
          vibration: true,
          preview: true
        },
        tasks: {
          enabled: true,
          reminders: ['15min', '1day'],
          sound: true,
          vibration: true
        },
        calendar: {
          enabled: true,
          reminders: ['15min', '1hour', '1day'],
          sound: true,
          vibration: true
        },
        deliveryMethod: ['push', 'email'],
        priority: 'normal',
        quietHoursEnabled: true
      }));
      setNotificationSettings(defaultSettings);
    }

    if (savedGlobalSettings) {
      try {
        setGlobalSettings(JSON.parse(savedGlobalSettings));
      } catch (e) {
        console.error('Failed to parse global settings', e);
      }
    }
  }, []);

  // Save settings to localStorage
  const handleSaveSettings = () => {
    try {
      localStorage?.setItem('notificationSettings', JSON.stringify(notificationSettings));
      localStorage?.setItem('globalNotificationSettings', JSON.stringify(globalSettings));
      setSaveStatus('success');
      setHasChanges(false);
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (e) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const updateMemberSettings = (memberId, updates) => {
    setNotificationSettings(prev => {
      const memberIndex = prev?.findIndex(s => s?.memberId === memberId);
      if (memberIndex >= 0) {
        const newSettings = [...prev];
        newSettings[memberIndex] = { ...newSettings?.[memberIndex], ...updates };
        return newSettings;
      } else {
        return [...prev, { memberId, ...updates }];
      }
    });
    setHasChanges(true);
  };

  const getMemberSettings = (memberId) => {
    return notificationSettings?.find(s => s?.memberId === memberId) || {
      memberId,
      enabled: true,
      messaging: { enabled: true, sound: true, vibration: true, preview: true },
      tasks: { enabled: true, reminders: ['15min', '1day'], sound: true, vibration: true },
      calendar: { enabled: true, reminders: ['15min', '1hour', '1day'], sound: true, vibration: true },
      deliveryMethod: ['push', 'email'],
      priority: 'normal',
      quietHoursEnabled: true
    };
  };

  const handleBulkAction = (action) => {
    if (action === 'enableAll') {
      setNotificationSettings(prev => 
        prev?.map(s => ({ ...s, enabled: true }))
      );
    } else if (action === 'disableAll') {
      setNotificationSettings(prev => 
        prev?.map(s => ({ ...s, enabled: false }))
      );
    } else if (action === 'resetAll') {
      const defaultSettings = familyMembers?.map(member => ({
        memberId: member?.id,
        enabled: true,
        messaging: { enabled: true, sound: true, vibration: true, preview: true },
        tasks: { enabled: true, reminders: ['15min', '1day'], sound: true, vibration: true },
        calendar: { enabled: true, reminders: ['15min', '1hour', '1day'], sound: true, vibration: true },
        deliveryMethod: ['push', 'email'],
        priority: 'normal',
        quietHoursEnabled: true
      }));
      setNotificationSettings(defaultSettings);
    }
    setHasChanges(true);
  };

  const handleTestNotification = (member) => {
    setPreviewMember(member);
    setShowPreview(true);
  };

  return (
    <>
      <Helmet>
        <title>Notifications - Starling Schedule</title>
      </Helmet>
      <div className="min-h-screen bg-background">
      <TabNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-h1 font-heading text-foreground mb-2">
                Notification Settings
              </h1>
              <p className="text-muted-foreground">
                Control alerts for messages, task reminders, and calendar events per family member
              </p>
            </div>
            <div className="flex gap-3">
              {hasChanges && (
                <Button
                  variant="outline"
                  onClick={() => window?.location?.reload()}
                >
                  <Icon name="x" size={16} className="mr-2" />
                  Cancel
                </Button>
              )}
              <Button
                onClick={handleSaveSettings}
                disabled={!hasChanges}
              >
                <Icon name="check" size={16} className="mr-2" />
                Save Settings
              </Button>
            </div>
          </div>

          {/* Save Status */}
          {saveStatus && (
            <div className={`mt-4 p-4 rounded-lg flex items-center gap-3 ${
              saveStatus === 'success' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
            }`}>
              <Icon 
                name={saveStatus === 'success' ? 'check-circle' : 'x-circle'} 
                size={20} 
              />
              <span>
                {saveStatus === 'success' ?'Settings saved successfully!' :'Failed to save settings. Please try again.'}
              </span>
            </div>
          )}
        </div>

        {/* Global Notification Panel */}
        <div className="mb-8">
          <GlobalNotificationPanel
            settings={globalSettings}
            onChange={(updates) => {
              setGlobalSettings(prev => ({ ...prev, ...updates }));
              setHasChanges(true);
            }}
          />
        </div>

        {/* Bulk Actions */}
        <div className="mb-6 flex flex-wrap gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleBulkAction('enableAll')}
          >
            <Icon name="check-circle" size={16} className="mr-2" />
            Enable All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleBulkAction('disableAll')}
          >
            <Icon name="x-circle" size={16} className="mr-2" />
            Disable All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleBulkAction('resetAll')}
          >
            <Icon name="rotate-ccw" size={16} className="mr-2" />
            Reset to Defaults
          </Button>
        </div>

        {/* Member Notification Cards */}
        <div className="space-y-4">
          {familyMembers?.map(member => (
            <MemberNotificationCard
              key={member?.id}
              member={member}
              settings={getMemberSettings(member?.id)}
              onUpdate={(updates) => updateMemberSettings(member?.id, updates)}
              onTest={() => handleTestNotification(member)}
            />
          ))}
        </div>
      </div>

      {/* Notification Preview Modal */}
      {showPreview && previewMember && (
        <NotificationPreview
          member={previewMember}
          settings={getMemberSettings(previewMember?.id)}
          onClose={() => {
            setShowPreview(false);
            setPreviewMember(null);
          }}
        />
      )}
    </div>
    </>
  );
};

export default NotificationSettingsManagement;